import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  createPreviewActions,
  ShareablePreview,
  type ShareablePreviewFeeLine,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import {
  DEFAULT_CURRENCY,
  majorToMinor,
  minorToMajor,
} from "@/core/utils/func/money-minor";
import { CUSTOMER_SEGMENTS } from "@/features/shared/api/enums";
import { useWalletSelectOptions } from "@/features/shared/api";
import { useResolveWalletQr } from "@/features/wallet/api/use-resolve-wallet-qr";
import { useScanTransfer } from "@/features/wallet/api/use-scan-transfer";
import { useTransactionFeeQuote } from "@/features/wallet/api/use-transaction-fee-quote";
import type {
  ScanTransferRequestDto,
  TransactionFeeQuoteResponseDto,
} from "@/features/wallet/api/dto";
import {
  createScanTransferSchema,
  type ScanTransferFormValues,
} from "@/features/wallet/schemas/scan-transfer.schema";

const EMPTY: ScanTransferFormValues = {
  qrToken: "",
  amount: 0,
  sourceWalletId: "",
  customerReference: "",
  customerSegment: "INDIVIDUAL",
};

const quoteFeeLines = (
  quote: TransactionFeeQuoteResponseDto,
  t: (key: string) => string
): ShareablePreviewFeeLine[] => [
  {
    label: t("wallet.transactions.preview.feeGross"),
    amount: minorToMajor(quote.grossFeeAmountMinor, quote.currency),
    currency: quote.currency,
  },
  ...(quote.discountAmountMinor > 0
    ? [
        {
          label: t("wallet.transactions.preview.feeDiscount"),
          amount: -minorToMajor(quote.discountAmountMinor, quote.currency),
          currency: quote.currency,
        },
      ]
    : []),
  {
    label: t("wallet.transactions.preview.feeNet"),
    amount: minorToMajor(quote.netFeeAmountMinor, quote.currency),
    currency: quote.currency,
  },
];

/**
 * Paiement par scan d'un QR client (guide v2.0) : resolve → devis → preview
 * avec frais → `scan-transfer` idempotent.
 */
export function ScanTransferPage() {
  const { t } = useTypedTranslation();
  const tw = (key: string) => t(key as TFunctionType);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language
  const schema = useMemo(() => createScanTransferSchema(), [i18n.language]);
  const scan = useScanTransfer();
  const feeQuote = useTransactionFeeQuote();
  const { options: walletOptions, isLoading: walletsLoading } =
    useWalletSelectOptions(true);
  const [tokenToResolve, setTokenToResolve] = useState("");
  const resolve = useResolveWalletQr(tokenToResolve, tokenToResolve.length > 0);
  const [preview, setPreview] = useState<{
    payload: ScanTransferRequestDto;
    quote: TransactionFeeQuoteResponseDto;
  } | null>(null);

  const form = useForm({
    defaultValues: EMPTY,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const token = parsed.data.qrToken.trim();
      setTokenToResolve(token);
      const resolved = resolve.data;
      if (resolved && resolved.walletId && parsed.data.sourceWalletId?.trim()) {
        if (resolved.walletId === parsed.data.sourceWalletId.trim()) {
          notifSilenceError(tw("wallet.qr.scan.selfTransfer"));
          return;
        }
      }
      if (resolved && resolved.canReceive === false) {
        notifSilenceError(tw("wallet.qr.scan.cannotReceive"));
        return;
      }
      const amountMinor = majorToMinor(parsed.data.amount, DEFAULT_CURRENCY);
      const sourceWalletId = parsed.data.sourceWalletId?.trim() || undefined;
      try {
        const quote = await feeQuote.mutateAsync({
          productCode: "WALLET_TO_WALLET",
          customerSegment: parsed.data.customerSegment,
          feePurpose: "CUSTOMER_FEE",
          amountMinor,
          currency: DEFAULT_CURRENCY,
          transactionReference: crypto.randomUUID(),
        });
        const payload: ScanTransferRequestDto = {
          qrToken: token,
          amountMinor,
          ...(sourceWalletId ? { sourceWalletId } : {}),
          ...(parsed.data.customerReference?.trim()
            ? { customerReference: parsed.data.customerReference.trim() }
            : {}),
        };
        setPreview({ payload, quote });
      } catch (error) {
        notifSilenceError(
          tw("wallet.transactions.fees.error") +
            " " +
            apiErrorHandler(error)
        );
      }
    },
  });

  const fieldError =
    (name: keyof ScanTransferFormValues) =>
    ({ value }: { value: unknown }) => {
      const result = schema.shape[name].safeParse(value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const confirm = () => {
    if (!preview) return;
    scan.mutate(preview.payload, {
      onSuccess: (tx) => {
        notifSilence(tw("wallet.qr.scan.feedback.initiated"));
        setPreview(null);
        navigate(`/app/wallet/transactions/${tx.id}`);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  return (
    <section className="flex max-w-2xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {tw("wallet.qr.scan.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {tw("wallet.qr.scan.description")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{tw("wallet.qr.scan.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="qrToken" validators={{ onChange: fieldError("qrToken") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="scan-token">{tw("wallet.qr.scan.token")}</Label>
                  <Input
                    id="scan-token"
                    placeholder={tw("wallet.qr.scan.tokenPlaceholder")}
                    value={field.state.value}
                    onBlur={() => {
                      field.handleBlur();
                      if (field.state.value.trim()) {
                        setTokenToResolve(field.state.value.trim());
                      }
                    }}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors[0] ? (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  ) : null}
                </div>
              )}
            </form.Field>

            {resolve.data ? (
              <dl className="grid grid-cols-1 gap-2 rounded-md border border-border p-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">
                    {tw("wallet.qr.scan.walletNumber")}
                  </dt>
                  <dd>
                    <NoTranslate as="span">
                      {resolve.data.walletNumber || resolve.data.walletId}
                    </NoTranslate>
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {tw("wallet.qr.scan.currency")}
                  </dt>
                  <dd>
                    <NoTranslate as="span">
                      {resolve.data.currency ?? DEFAULT_CURRENCY}
                    </NoTranslate>
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {tw("wallet.qr.scan.canReceive")}
                  </dt>
                  <dd>
                    {resolve.data.canReceive
                      ? tw("wallet.qr.scan.canReceive")
                      : tw("wallet.qr.scan.cannotReceive")}
                  </dd>
                </div>
              </dl>
            ) : null}

            <form.Field name="amount" validators={{ onChange: fieldError("amount") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="scan-amount">{tw("wallet.qr.scan.amount")}</Label>
                  <Input
                    id="scan-amount"
                    type="number"
                    min={1}
                    placeholder={tw("wallet.qr.scan.amountPlaceholder")}
                    value={field.state.value || ""}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value === "" ? 0 : Number(e.target.value)
                      )
                    }
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="sourceWalletId">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{tw("wallet.qr.scan.sourceWalletId")}</Label>
                  <EntitySearchableSelect
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    options={walletOptions}
                    isLoading={walletsLoading}
                    clearable
                    placeholder={tw("wallet.qr.scan.sourceWalletIdPlaceholder")}
                  />
                  <p className="text-sm text-muted-foreground">
                    {tw("wallet.qr.scan.sourceWalletIdHint")}
                  </p>
                </div>
              )}
            </form.Field>

            <form.Field name="customerSegment">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{tw("wallet.qr.scan.customerSegment")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as ScanTransferFormValues["customerSegment"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_SEGMENTS.map((seg) => (
                        <SelectItem key={seg} value={seg}>
                          {seg}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="customerReference">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="scan-ref">
                    {tw("wallet.qr.scan.customerReference")}
                  </Label>
                  <Input
                    id="scan-ref"
                    placeholder={tw("wallet.qr.scan.customerReferencePlaceholder")}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Subscribe selector={(s) => s.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting || feeQuote.isPending}>
                  {isSubmitting || feeQuote.isPending ? <ButtonSpinner /> : null}
                  {tw("wallet.qr.scan.submit")}
                </Button>
              )}
            </form.Subscribe>
          </form>
        </CardContent>
      </Card>

      {preview ? (
        <ShareablePreview
          data={preview.payload}
          title={tw("wallet.qr.scan.preview.title")}
          description={tw("wallet.qr.scan.preview.description")}
          mode="user"
          defaultOpen
          fees={quoteFeeLines(preview.quote, tw)}
          total={{
            label: tw("wallet.transactions.preview.totalLabel"),
            amount:
              minorToMajor(preview.payload.amountMinor, DEFAULT_CURRENCY) +
              minorToMajor(preview.quote.netFeeAmountMinor, preview.quote.currency),
            currency: preview.quote.currency,
          }}
          onOpenChange={(open) => {
            if (!open && !scan.isPending) setPreview(null);
          }}
          actions={createPreviewActions({
            onConfirm: confirm,
            onCancel: () => setPreview(null),
            t: (key) => t(key),
            confirmLoading: scan.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </section>
  );
}
